import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

/**
 * AiContext — lets any page tell Ask Mantle what the user is working on.
 *
 * A page registers a descriptor:
 *   { type: 'invoice'|'estimate'|'document'|'client'|'project'|..., id, label,
 *     editable?: boolean,
 *     getSnapshot: () => plain object sent to the AI,
 *     applyPatch?: (patch) => void,   // live-apply an AI edit in the editor
 *     undo?: () => void }             // "undo that last change"
 *
 * Handlers are kept in a ref so the panel always calls the latest closure
 * without re-registering on every render.
 */
const AiCtx = createContext(null);

export function AiContextProvider({ children }) {
  const [meta, setMeta] = useState(null); // {type,id,label,editable}
  const handlersRef = useRef(null);

  const register = useCallback((desc) => {
    handlersRef.current = desc;
    setMeta(prev => {
      const next = { type: desc.type, id: desc.id, label: desc.label, editable: !!desc.applyPatch };
      if (prev && prev.type === next.type && prev.id === next.id && prev.label === next.label && prev.editable === next.editable) return prev;
      return next;
    });
  }, []);

  const unregister = useCallback((key) => {
    const cur = handlersRef.current;
    if (cur && `${cur.type}:${cur.id}` === key) {
      handlersRef.current = null;
      setMeta(null);
    }
  }, []);

  const value = useMemo(() => ({
    context: meta,
    register,
    unregister,
    getSnapshot: () => { try { return handlersRef.current?.getSnapshot?.() || null; } catch { return null; } },
    applyPatch: (patch) => handlersRef.current?.applyPatch?.(patch),
    undo: () => handlersRef.current?.undo?.(),
  }), [meta, register, unregister]);

  return <AiCtx.Provider value={value}>{children}</AiCtx.Provider>;
}

export function useAiContext() {
  return useContext(AiCtx) || {
    context: null, register: () => {}, unregister: () => {},
    getSnapshot: () => null, applyPatch: () => {}, undo: () => {},
  };
}

/** Pages call this with a descriptor (or null to clear). Re-registers when type/id/label change. */
export function useRegisterAiContext(desc) {
  const { register, unregister } = useAiContext();
  const descRef = useRef(desc);
  descRef.current = desc;
  const key = desc ? `${desc.type}:${desc.id}` : null;
  const label = desc?.label || '';
  const editable = !!desc?.applyPatch;

  useEffect(() => {
    if (!key) return undefined;
    register({
      type: descRef.current.type,
      id: descRef.current.id,
      label: descRef.current.label,
      getSnapshot: () => descRef.current?.getSnapshot?.(),
      applyPatch: editable ? (p) => descRef.current?.applyPatch?.(p) : undefined,
      undo: () => descRef.current?.undo?.(),
    });
    return () => unregister(key);
  }, [key, label, editable, register, unregister]);
}
